import { Lock, PaperPlaneTilt } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

interface ChatComposerProps {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  maxLength: number
  hintKey: string
  sending?: boolean
  readOnly?: boolean
  disabled?: boolean
}

/**
 * Shared composer for activity (1000) and private (2000) chats. The limit is
 * mirrored in `maxLength` and validated again before sending; the server stays
 * authoritative. Read-only chats keep the timeline but replace the input with
 * the approved 7-day explanation.
 */
export function ChatComposer({
  value,
  onChange,
  onSend,
  maxLength,
  hintKey,
  sending = false,
  readOnly = false,
  disabled = false,
}: ChatComposerProps) {
  const { t } = useTranslation()

  if (readOnly) {
    return (
      <div className="chat-readonly" role="note">
        <Lock size={20} weight="bold" aria-hidden="true" />
        <div>
          <p className="chat-readonly__title">{t('chat.activity.readOnlyTitle')}</p>
          <p className="chat-readonly__body">{t('chat.activity.readOnlyBody')}</p>
        </div>
      </div>
    )
  }

  const trimmed = value.trim()
  const sendDisabled = disabled || sending || trimmed.length === 0
  const showCounter = value.length > maxLength * 0.75

  function submit() {
    if (sendDisabled) return
    onSend()
  }

  return (
    <form
      className="chat-composer"
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <div className="chat-composer__field">
        <label className="sr-only" htmlFor="chat-composer-input">
          {t(hintKey)}
        </label>
        <textarea
          id="chat-composer-input"
          className={`chat-composer__input ${showCounter ? 'chat-composer__input--counter' : ''}`}
          value={value}
          placeholder={t(hintKey)}
          maxLength={maxLength}
          rows={1}
          // Never disable the field while sending: a disabled focused input
          // loses focus and the mobile keyboard closes mid-conversation.
          aria-busy={sending}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (
              event.key === 'Enter' &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault()
              submit()
            }
          }}
          style={{ height: '48px', maxHeight: '48px', overflowY: 'auto' }}
        />
        {showCounter ? (
          <span className="chat-composer__counter" aria-hidden="true">
            {t('chat.composer.counter', {
              count: value.length,
              max: maxLength,
            })}
          </span>
        ) : null}
      </div>
      <button
        type="submit"
        className="chat-composer__send"
        disabled={sendDisabled}
        aria-label={t('chat.composer.send')}
        aria-busy={sending}
        // Keep the textarea focused (and the keyboard open) on mobile: the
        // default pointer-down focuses the button and blurs the composer.
        onPointerDown={(event) => event.preventDefault()}
      >
        {sending ? (
          <span className="spinner size-5" aria-hidden="true" />
        ) : (
          <PaperPlaneTilt size={20} weight="bold" aria-hidden="true" />
        )}
      </button>
      {disabled ? (
        <p className="chat-composer__offline" role="status">
          {t('chat.composer.offline')}
        </p>
      ) : null}
    </form>
  )
}
